import React, { useEffect, useState } from "react";
import api, { formatINR, API } from "../lib/api";
import { Trophy, TrendUp } from "@phosphor-icons/react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function SalesLeaderboard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/sales/leaderboard", { params: { year, month } })
      .then(({ data }) => setRows(data.rows || []))
      .catch(() => setRows([]));
  }, [year, month]);

  if (rows === null) return null;

  return (
    <div className="bg-white border border-[#E5E7EB]" data-testid="sales-leaderboard">
      <div className="px-4 sm:px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Trophy size={16} color="#FBBF24" weight="fill" />
          <div>
            <div className="label-caps">Leaderboard · {MONTHS[month - 1]} {year}</div>
            <div className="font-display font-bold text-xl mt-1">Sales rank by achievement %</div>
          </div>
        </div>
        <div className="flex gap-2">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                  data-testid="lb-month" className="border border-[#E5E7EB] px-2 py-2 text-sm">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                  data-testid="lb-year" className="border border-[#E5E7EB] px-2 py-2 text-sm">
            {[now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#9CA3AF]" data-testid="lb-empty">
          No sales employees found for this period.
        </div>
      ) : (
        <ul className="divide-y divide-[#F3F4F6]">
          {rows.map((r) => {
            const pct = r.achievement_pct;
            const barColor = pct >= 100 ? "#10B981" : pct >= 60 ? "#FBBF24" : "#DC2626";
            const medal = r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`;
            return (
              <li key={r.id} className="px-6 py-4 flex items-center gap-4" data-testid={`lb-row-${r.employee_id}`}>
                <div className="w-10 text-center font-display font-black text-lg">{medal}</div>
                <div className="w-10 h-10 rounded-full overflow-hidden bg-[#106109]/10 border border-[#106109]/25 flex items-center justify-center flex-shrink-0">
                  {r.photo ? (
                    <img src={`${API}/employees/${r.id}/photo`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-display font-black text-[#106109]">
                      {(r.name || "").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?"}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#111827] truncate">{r.name}</div>
                  <div className="text-xs text-[#6B7280] font-mono">
                    {r.employee_id} · <TrendUp size={10} className="inline" /> {formatINR(r.achieved)} of {formatINR(r.target)}
                  </div>
                </div>
                <div className="min-w-[100px] text-right">
                  <div className="font-display font-black text-xl" style={{ color: barColor }}>
                    {r.target > 0 ? `${pct}%` : "—"}
                  </div>
                  <div className="mt-1 h-1.5 w-24 bg-[#F3F4F6] overflow-hidden">
                    <div className="h-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, background: barColor }} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
